import { Topic } from '@/@types/topic'
import { MenuItemData } from '@/components/DropdownMenu/types'
import BasicListItem from '@/components/List/ListItem'
import ListSection from '@/components/List/ListSection'
import { showConfirm } from '@/components/Modal/utils'
import MoreMenuButton from '@/components/MoreMenuButton'
import Page from '@/components/Page'
import {
	getSubscribedTopicsQueryOptions,
	getTopicsToppingQueryOptions,
} from '@/utils/fetchers/topic'
import {
	useChangeTopicSubscriptionStatusToggle,
	useToppingShortcutTopicToggle,
} from '@/utils/hooks/useTopicActionHandlers'
import useTopics from '@/utils/hooks/useTopics'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { prepareToRefetchListQuery } from '@/utils/list'
import { css } from '@emotion/react'
import { ExitIcon, StickStickIcon, StickUnstickIcon } from '@ruguoapp/jike-fe-icons'
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { HTMLMotionProps, LayoutGroup, motion } from 'motion/react'
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { useTitle } from 'react-use'

enum MoreAction {
	UNPIN = 'UNPIN',
	PIN = 'PIN',
	UNSUBSCRIBE = 'UNSUBSBRIBE',
}

interface ListItemProps extends HTMLMotionProps<'div'> {
	data: Topic
}
function ListItem({ data, ...props }: ListItemProps) {
	const moreMenuData: MenuItemData[] = useMemo(() => {
		const items: MenuItemData[] = []
		if (data.inShortcuts) {
			items.push({
				id: MoreAction.UNPIN,
				title: '取消置顶',
				icon: <StickUnstickIcon />,
			})
		} else {
			items.push({
				id: MoreAction.PIN,
				title: '置顶',
				icon: <StickStickIcon />,
			})
		}
		items.push({
			id: MoreAction.UNSUBSCRIBE,
			title: '退出圈子',
			icon: <ExitIcon />,
		})
		return items
	}, [data])
	const { toggleToppingShortcut } = useToppingShortcutTopicToggle(data.id)
	const { toggleSubscriptionStatus } = useChangeTopicSubscriptionStatusToggle(data.id)

	const handleMoreMenuSelect = async (actionId: string) => {
		// 根据不同的操作类型执行相应的逻辑
		switch (actionId) {
			case MoreAction.PIN:
				await toggleToppingShortcut()
				toast.success('已置顶')
				break
			case MoreAction.UNPIN:
				await toggleToppingShortcut()
				toast.success('已取消置顶')
				break

			case MoreAction.UNSUBSCRIBE:
				await showConfirm('确定退出本圈子吗？')
				await toggleSubscriptionStatus()
				toast.success('已退出圈子')
				break
			default:
				break
		}
	}

	return (
		<BasicListItem
			css={css`
				grid-template-columns: 48px 1fr auto;
			`}
			{...props}
		>
			<img
				src={data.squarePicture.thumbnailUrl}
				css={css`
					border-radius: 4.571px;
					border: 1px solid var(--tint-picBorder, rgba(0, 0, 0, 0.05));
					width: 48px;
					height: 48px;
					display: block;
				`}
			/>
			<div
				css={css`
					overflow: hidden;
				`}
			>
				<div
					css={css`
						color: var(--tint-primary, #333);

						/* H3 Heading */
						font-family: 'Noto Sans SC';
						font-size: 16px;
						font-style: normal;
						font-weight: 500;
						line-height: 25px; /* 156.25% */
						letter-spacing: 0.25px;

						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
					`}
				>
					{data.content}
				</div>

				<div
					css={css`
						margin-top: 2px;
						color: var(--tint-primary, #333);

						/* Body 4 */
						font-family: 'Noto Sans SC';
						font-size: 14px;
						font-style: normal;
						font-weight: 400;
						line-height: 20px; /* 142.857% */
						letter-spacing: 0.25px;

						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
					`}
				>
					{data.briefIntro}
				</div>
			</div>
			<MoreMenuButton data={moreMenuData} onSelect={handleMoreMenuSelect} />
		</BasicListItem>
	)
}

export const Route = createFileRoute('/u/$username/topic')({
	component: RouteComponent,
})

function RouteComponent() {
	const { username } = Route.useParams()
	const { data: toppingTopicIds } = useSuspenseQuery(getTopicsToppingQueryOptions())
	const { data, fetchNextPage, refetch, isFetching } = useSuspenseInfiniteQuery(
		getSubscribedTopicsQueryOptions(username)
	)
	const topicIds = useMemo(() => data.pages.flatMap((page) => page.data), [data.pages])
	const toppingTopics = useTopics(toppingTopicIds)
	const topics = useTopics(topicIds)

	const refresh = () => {
		prepareToRefetchListQuery(getSubscribedTopicsQueryOptions(username).queryKey)
		return refetch()
	}

	const navigate = useUniversalNavigate()

	useTitle(`我的圈子 - 即刻`)

	return (
		<Page onReachPageBottom={fetchNextPage}>
			<motion.div
				css={css`
					padding: 0 15px 20px;
					.title {
						color: var(--tint-primary);
					}
				`}
			>
				<LayoutGroup>
					<ListSection title="我置顶的圈子" layout="position">
						{toppingTopics.map((item) => (
							<ListItem
								layout="position"
								key={item.id}
								data={item}
								data-clickable-feedback
								onClick={() => {
									navigate({
										to: '/topic/$id',
										params: {
											id: item.id,
										},
									})
								}}
							/>
						))}
					</ListSection>
					<ListSection title="我加入的圈子" layout="position">
						{topics.map((item) => (
							<ListItem
								layout="position"
								key={item.id}
								data={item}
								data-clickable-feedback
								onClick={() => {
									navigate({
										to: '/topic/$id',
										params: {
											id: item.id,
										},
									})
								}}
							/>
						))}
					</ListSection>
				</LayoutGroup>
			</motion.div>
		</Page>
	)
}
